/**
 * Главная исполнителя.
 *
 * Отвечает на единственный вопрос: что делать прямо сейчас.
 * Одна крупная карточка с текущей задачей и одна кнопка действия.
 * Остальная очередь свёрнута ниже — она не должна конкурировать
 * за внимание с текущей работой.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Pill, Row, toast } from '../components/ui';
import { money, untilDeadline } from '../lib/format';
import type { LedgerEntry, Video } from '../lib/types';

type FinanceMe = {
  balance: number;
  month: { earned: number; penalties: number; paidOut: number };
  history: LedgerEntry[];
};

/** Что человеку предлагается сделать в каждом статусе */
const ACTION: Record<string, string> = {
  NEW: 'Взять в работу',
  SHOOTING: 'Загрузить материал',
  AWAITING_EDIT: 'Взять в монтаж',
  EDITING: 'Загрузить готовое видео',
  REVISION: 'Исправить и перезалить',
};

export function WorkerHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: queue, isLoading } = useQuery({
    queryKey: ['my-queue'],
    queryFn: () => api.get<Video[]>('/api/videos/queue'),
  });

  const { data: finance } = useQuery({
    queryKey: ['finance-me'],
    queryFn: () => api.get<FinanceMe>('/api/finance/me'),
  });

  const start = useMutation({
    mutationFn: (id: string) => api.post(`/api/videos/${id}/start`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-queue'] });
      toast('Задача взята в работу', '▸');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const current = queue?.[0];
  const isEditorStage = current
    ? ['AWAITING_EDIT', 'EDITING', 'REVISION'].includes(current.status)
    : false;

  return (
    <>
      <PageHeader title="Главная" sub={user?.displayName} />
      <Page>
        {isLoading ? (
          <Loading rows={2} />
        ) : !current ? (
          <div className="card">
            <Empty
              title="Задач нет"
              hint="Всё сдано. Новое ТЗ придёт уведомлением в Telegram."
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
            <div>
              <CurrentTask
                video={current}
                isEditorStage={isEditorStage}
                onAction={() => {
                  // Взять в работу можно прямо отсюда, а для загрузки
                  // всё равно нужен экран видео
                  if (current.status === 'NEW' || current.status === 'AWAITING_EDIT') {
                    start.mutate(current.id);
                  } else {
                    navigate(`/videos/${current.id}`);
                  }
                }}
                busy={start.isPending}
              />

              {queue && queue.length > 1 && (
                <>
                  <div className="sec">Дальше в очереди · {queue.length - 1}</div>
                  <div className="card p-0 overflow-hidden">
                    {queue.slice(1).map((v) => {
                      const d = untilDeadline(v.currentDeadline ?? v.deadlineShootAt);
                      const edit = ['AWAITING_EDIT', 'EDITING', 'REVISION'].includes(v.status);
                      return (
                        <Link
                          key={v.id}
                          to={`/videos/${v.id}`}
                          className="flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24] last:border-0 hover:bg-white/[0.03] transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{v.task.title}</div>
                            <div className="text-[11.5px] text-muted2">
                              ТЗ #{v.task.number} · видео {v.index}
                            </div>
                          </div>
                          <Pill status={v.status} />
                          <div className={`text-xs w-32 text-right ${d.overdue ? 'text-dang' : 'text-muted'}`}>
                            {d.text}
                          </div>
                          <div className="mono font-bold w-20 text-right">
                            {money(edit ? v.priceEdit : v.priceShoot)}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div>
              <div className="sec">Баланс</div>
              <div className="card">
                <div className="mono text-[29px] font-extrabold tracking-[-0.03em]">
                  {finance ? money(finance.balance) : '—'}
                </div>
                <div className="text-[11.5px] text-muted2 mt-1">Выплата раз в неделю</div>
                <Link to="/finance" className="btn btn-sm w-full mt-3 block text-center">
                  История
                </Link>
              </div>

              <div className="sec">За месяц</div>
              <div className="card">
                <Row label="Заработано">
                  <span className="mono text-ok">
                    {finance ? money(finance.month.earned) : '—'}
                  </span>
                </Row>
                <Row label="Штрафы">
                  <span className="mono text-dang">
                    {finance ? money(finance.month.penalties) : '—'}
                  </span>
                </Row>
                <Row label="Выплачено">
                  <span className="mono">{finance ? money(finance.month.paidOut) : '—'}</span>
                </Row>
              </div>
            </div>
          </div>
        )}
      </Page>
    </>
  );
}

function CurrentTask({
  video,
  isEditorStage,
  onAction,
  busy,
}: {
  video: Video;
  isEditorStage: boolean;
  onAction: () => void;
  busy: boolean;
}) {
  const deadline = untilDeadline(video.currentDeadline ?? video.deadlineShootAt);
  const price = isEditorStage ? video.priceEdit : video.priceShoot;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#3B2E70] p-6 bg-gradient-to-br from-[#241948] via-[#181231] to-[#13131C]">
      <div className="absolute w-[340px] h-[340px] -right-32 -top-44 rounded-full bg-[radial-gradient(circle,#7C5CFF33,transparent_70%)]" />

      <div className="relative">
        <div className="text-[10.5px] tracking-[0.17em] text-accent2 font-bold">
          СЕЙЧАС В РАБОТЕ
        </div>
        <div className="text-4xl font-extrabold tracking-[-0.035em] mt-2.5 mb-1.5">
          {video.task.title}
        </div>
        <div className={`text-[13.5px] ${deadline.overdue ? 'text-dang' : 'text-[#B4AFD0]'}`}>
          {deadline.overdue && '⚠ '}
          {deadline.text}
          {video.revisionCount > 0 && ` · доработка ${video.revisionCount}`}
        </div>

        <div className="flex items-center gap-3 mt-5">
          <Pill status={video.status} />
          <span className="mono text-[19px] font-extrabold">{money(price)}</span>
          <div className="flex-1" />
          <button className="btn btn-primary" onClick={onAction} disabled={busy}>
            {busy ? '…' : (ACTION[video.status] ?? 'Открыть')}
          </button>
          <Link to={`/videos/${video.id}`} className="btn">
            Открыть
          </Link>
        </div>
      </div>
    </div>
  );
}
